import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
} from "recharts";
import { getAnalyticsSummary, listQRCodes } from "../services/api";

const COLORS = [
  "#0046FF", "#00B8D9", "#1E9E5A", "#F59E0B", "#8B5CF6",
  "#EC4899", "#F97316", "#14B8A6", "#6366F1", "#84CC16",
];

const QrIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <path d="M14 14h3v3h-3zM20 14v.01M14 20h3M20 17v4" />
  </svg>
);

const ScanIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3" />
    <path d="M4 12h16" />
  </svg>
);

const TrendIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 17l6-6 4 4 8-8" />
    <path d="M14 7h7v7" />
  </svg>
);

function PieTooltip({ active, payload, total }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  const share = total ? Math.round((row.value / total) * 100) : 0;
  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-label">{row.name}</div>
      <div className="chart-tooltip-row">
        <span className="chart-tooltip-dot" style={{ background: row.fill }} />
        <span className="chart-tooltip-name">Scans</span>
        <span className="chart-tooltip-value">{row.value}</span>
      </div>
      <div className="chart-tooltip-row">
        <span className="chart-tooltip-dot" style={{ background: "#9CA3AF" }} />
        <span className="chart-tooltip-name">Share of total</span>
        <span className="chart-tooltip-value">{share}%</span>
      </div>
    </div>
  );
}

// Small ascending/descending arrow shown next to the active sorted column heading.
const SortArrow = ({ column, sortKey, sortDir }) => {
  if (sortKey !== column) return null;
  return <span className="sort-arrow">{sortDir === "asc" ? "↑" : "↓"}</span>;
};

const fmtDateTime = (value) =>
  value
    ? new Date(value).toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric"
      })
    : "—";

export default function Dashboard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState({ total_qr_codes: 0, total_scans: 0 });
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  // null | 'qrcodes' | 'scans'
  const [activeView, setActiveView] = useState(null);

  // Sorting state for the "Your QR Codes" table.
  // sortKey: 'title' | 'created_at' | 'scan_count' | null
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc"); // 'asc' | 'desc'

  // 3-click cycle per column: 1st click -> ascending, 2nd click -> descending,
  // 3rd click -> back to default (unsorted, original order).
  const handleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortKey(null);
      setSortDir("asc");
    }
  };

  useEffect(() => {
    Promise.all([getAnalyticsSummary(), listQRCodes()])
      .then(([summaryRes, qrRes]) => {
        setSummary(summaryRes.data);
        setQrCodes(qrRes.data.results || qrRes.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const totalQrCodes = summary.total_qr_codes ?? qrCodes.length;
  const totalScans = summary.total_scans ?? qrCodes.reduce((s, qr) => s + (qr.scan_count || 0), 0);
  const avgScans = totalQrCodes ? (totalScans / totalQrCodes).toFixed(1) : "0";

  const pieData = useMemo(
    () =>
      qrCodes.map((qr, i) => ({
        name: qr.title,
        value: qr.scan_count,
        fill: COLORS[i % COLORS.length],
      })),
    [qrCodes]
  );

  // Sorted copy of qrCodes for the "Your QR Codes" table. Leaves the original
  // qrCodes array untouched so pieData stays correct.
  const sortedQrCodes = useMemo(() => {
    if (!sortKey) return qrCodes;
    const dir = sortDir === "asc" ? 1 : -1;

    const getValue = (qr) => {
      switch (sortKey) {
        case "title":
          return (qr.title || "").toLowerCase();
        case "created_at":
          return qr.created_at ? new Date(qr.created_at).getTime() : 0;
        case "scan_count":
          return qr.scan_count || 0;
        default:
          return "";
      }
    };

    return [...qrCodes].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [qrCodes, sortKey, sortDir]);

  const toggleView = (view) => setActiveView((v) => (v === view ? null : view));

  const cardStyle = (view) => ({
    cursor: "pointer",
    ...(activeView === view
      ? { boxShadow: "0 0 0 3px rgba(0,70,255,0.25), 0 12px 32px rgba(16,24,40,0.12)" }
      : {}),
  });

  return (
    <div>
      <div className="page-title">Dashboard</div>
      <div className="page-subtitle">Overview of your QR codes and scan activity</div>

      <div className="grid grid-3" style={{ marginBottom: 24 }}>
        <div
          className="card stat-card stat-blue"
          style={cardStyle("qrcodes")}
          onClick={() => toggleView("qrcodes")}
        >
          <div className="stat-head"><QrIcon /> Total QR Codes</div>
          <div className="stat-value">{totalQrCodes}</div>
          <div className="stat-foot">Across your account</div>
          <div className="stat-watermark"><QrIcon size={96} /></div>
        </div>

        <div
          className="card stat-card stat-green"
          style={cardStyle("scans")}
          onClick={() => toggleView("scans")}
        >
          <div className="stat-head"><ScanIcon /> Total Scans</div>
          <div className="stat-value">{totalScans}</div>
          <div className="stat-foot">All time</div>
          <div className="stat-watermark"><ScanIcon size={96} /></div>
        </div>

        <div
          className="card stat-card stat-purple"
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/analytics")}
        >
          <div className="stat-head"><TrendIcon /> Avg. Scans per QR</div>
          <div className="stat-value">{avgScans}</div>
          <div className="stat-foot">Total scans ÷ QR codes</div>
          <div className="stat-watermark"><TrendIcon size={96} /></div>
        </div>
      </div>

      {loading ? (
        <div className="card"><p>Loading...</p></div>
      ) : activeView === "qrcodes" ? (
        <div className="card table-card stat-blue">
          <div className="card-title">
            <span className="card-title-icon"><QrIcon size={18} /></span>
            Your QR Codes
          </div>
          {qrCodes.length === 0 ? (
            <p style={{ color: "#6B7280" }}>No QR codes yet. Create your first one!</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th className="sortable-th" onClick={() => handleSort("title")}>
                    Title <SortArrow column="title" sortKey={sortKey} sortDir={sortDir} />
                  </th>
                  <th className="sortable-th" onClick={() => handleSort("created_at")}>
                    Created <SortArrow column="created_at" sortKey={sortKey} sortDir={sortDir} />
                  </th>
                  <th className="sortable-th" onClick={() => handleSort("scan_count")}>
                    Scans <SortArrow column="scan_count" sortKey={sortKey} sortDir={sortDir} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedQrCodes.map((qr) => (
                  <tr key={qr.id}>
                    <td>{qr.title}</td>
                    <td className="cell-muted">{fmtDateTime(qr.created_at)}</td>
                    <td>{qr.scan_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : activeView === "scans" ? (
        <div className="card chart-card">
          <div className="card-title">
            <span className="card-title-icon"><ScanIcon size={18} /></span>
            Scans by QR Code
          </div>
          {pieData.length === 0 ? (
            <p style={{ color: "#6B7280" }}>No scan data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={380}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={130}
                  label={false}
                  labelLine={false}
                >
                  {pieData.map((row) => (
                    <Cell key={row.name} fill={row.fill} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip total={totalScans} />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      ) : (
        <div className="card">
          <p style={{ color: "#6B7280", margin: 0 }}>
            Click "Total QR Codes" or "Total Scans" above to see the details.
          </p>
        </div>
      )}
    </div>
  );
}
